
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, Check, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  shortTerm: z.string().min(10, {
    message: "Short-term goals must be at least 10 characters long.",
  }),
  mediumTerm: z.string().min(10, {
    message: "Medium-term goals must be at least 10 characters long.",
  }),
  longTerm: z.string().min(10, {
    message: "Long-term goals must be at least 10 characters long.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export interface InvestmentGoalsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InvestmentGoalsDialog: React.FC<InvestmentGoalsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { toast } = useToast();
  const [complianceResults, setComplianceResults] = useState<{
    shortTerm: { status: 'pass' | 'warning' | 'fail', message: string } | null,
    mediumTerm: { status: 'pass' | 'warning' | 'fail', message: string } | null,
    longTerm: { status: 'pass' | 'warning' | 'fail', message: string } | null,
  }>({
    shortTerm: null,
    mediumTerm: null,
    longTerm: null,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shortTerm: localStorage.getItem('investmentGoals.shortTerm') || "",
      mediumTerm: localStorage.getItem('investmentGoals.mediumTerm') || "",
      longTerm: localStorage.getItem('investmentGoals.longTerm') || "",
    },
  });

  const runComplianceCheck = async (values: FormValues) => {
    // In a real implementation, this would call an API to perform the compliance check
    // For demonstration, we'll simulate the check with some basic rules

    const mockComplianceCheck = (text: string) => {
      const lowercaseText = text.toLowerCase();
      
      if (lowercaseText.includes('high risk') || lowercaseText.includes('aggressive')) {
        return { 
          status: 'warning' as const, 
          message: 'Your goals indicate a high risk tolerance. Please ensure this aligns with your financial situation.' 
        };
      } else if (lowercaseText.includes('guaranteed') || lowercaseText.includes('certain')) {
        return { 
          status: 'fail' as const, 
          message: 'Terms like "guaranteed" or "certain" imply unrealistic expectations in investments.' 
        };
      } else {
        return { 
          status: 'pass' as const, 
          message: 'Your goals appear to be compliant with best practices.' 
        };
      }
    };

    // Run checks on each timeframe
    setComplianceResults({
      shortTerm: mockComplianceCheck(values.shortTerm),
      mediumTerm: mockComplianceCheck(values.mediumTerm),
      longTerm: mockComplianceCheck(values.longTerm),
    });
    
    toast({
      title: "Compliance Check Completed",
      description: "Review your investment goals for any compliance concerns.",
    });
  };

  const onSubmit = (values: FormValues) => {
    // Save values to localStorage
    localStorage.setItem('investmentGoals.shortTerm', values.shortTerm);
    localStorage.setItem('investmentGoals.mediumTerm', values.mediumTerm);
    localStorage.setItem('investmentGoals.longTerm', values.longTerm);
    
    // Run compliance check
    runComplianceCheck(values);
  };

  const getStatusIcon = (status: 'pass' | 'warning' | 'fail' | null) => {
    if (status === 'pass') return <Check className="h-4 w-4 text-green-500" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    if (status === 'fail') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Investment Goals</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="shortTerm"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Short-term Goals (0-2 years)</FormLabel>
                    {complianceResults.shortTerm && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" type="button" className="h-5 w-5 p-0">
                            {getStatusIcon(complianceResults.shortTerm.status)}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="font-medium">{complianceResults.shortTerm.message}</div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <FormControl>
                    <Textarea {...field} placeholder="Describe your short-term investment goals" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="mediumTerm"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Medium-term Goals (2-5 years)</FormLabel>
                    {complianceResults.mediumTerm && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" type="button" className="h-5 w-5 p-0">
                            {getStatusIcon(complianceResults.mediumTerm.status)}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="font-medium">{complianceResults.mediumTerm.message}</div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <FormControl>
                    <Textarea {...field} placeholder="Describe your medium-term investment goals" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="longTerm"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Long-term Goals (5+ years)</FormLabel>
                    {complianceResults.longTerm && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" type="button" className="h-5 w-5 p-0">
                            {getStatusIcon(complianceResults.longTerm.status)}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="font-medium">{complianceResults.longTerm.message}</div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <FormControl>
                    <Textarea {...field} placeholder="Describe your long-term investment goals" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">Save & Run Compliance Check</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
